'use client'

import { ID } from '@/core/types/models/id.model'
import {
  useOrderSummaryStore,
  useReservationStore,
} from '@/features/reservation/store'
import { useState } from 'react'

import { useMutation } from '@/core/hooks/use-mutation'
import { cn } from '@/core/lib/utils/cn'
import { Button } from '@/core/ui/atoms/button'
import { Checkbox } from '@/core/ui/forms/checkbox'
import { FormInput } from '@/core/ui/forms/input'
import { useTranslations } from 'next-intl'
import { useFormContext } from 'react-hook-form'
import { checkPromoCode } from '../../providers/client'

type Props = {
  tripId: ID
}

export const PromoCodeInput: React.FC<Props> = ({ tripId }) => {
  const t = useTranslations()
  const { setPromoCodePrice, setPaymentDetails } = useReservationStore()
  const setOrderSummaryField = useOrderSummaryStore(state => state.setField)
  const { getValues, setValue, control } = useFormContext()
  const promoCodeInitialValue = Boolean(getValues('promoCode'))
  const [addPromoCode, setAddPromoCode] = useState(
    Boolean(promoCodeInitialValue),
  )

  const { mutate, isPending, error } = useMutation({
    mutation: checkPromoCode,
    onSuccess(data) {
      const totalPrice = data?.totalPrice
      if (totalPrice) {
        setPromoCodePrice(totalPrice)
        setOrderSummaryField('promoCodePrice', totalPrice)
        setPaymentDetails(null)
      }
    },
  })

  const handleCheckPromoCode = () => {
    mutate({
      tripId,
      promoCode: getValues('promoCode'),
    })
  }

  return (
    <div>
      <Checkbox
        radius='sm'
        color='secondary'
        isSelected={addPromoCode}
        name='addPromoCode'
        classNames={{
          icon: 'text-white text-lg font-bold stroke-4',
        }}
        onValueChange={checked => {
          setAddPromoCode(checked)
          if (!checked) {
            setValue('promoCode', '')
            setPromoCodePrice(0)
            setOrderSummaryField('promoCodePrice', 0)
            setPaymentDetails(null)
          }
        }}
      >
        {t('forms.addPromoCode')}
      </Checkbox>

      {addPromoCode && (
        <div
          className={cn(
            'my-2 flex justify-between gap-3',

            error ? 'items-center' : 'items-end',
          )}
        >
          <FormInput
            control={control}
            name='promoCode'
            label={t('forms.promoCodeLabel')}
            placeholder={t('forms.promoCodePH')}
            className='flex-1'
            errorMessage={error}
            error={Boolean(error)}
          />
          <Button
            variant='outlined'
            color='sky-blue'
            type='button'
            className='h-11 w-22'
            radius='xs'
            onClick={handleCheckPromoCode}
            loading={isPending}
          >
            {t('actions.apply')}
          </Button>
        </div>
      )}
    </div>
  )
}
